import { useEffect, useMemo } from 'react'
import { useApp, POLICIES_MAP, PREFERENCE, NARRATIVE } from './store/useApp'
import { calculate } from './domain/probability'
import { detectStage, type LifeStage } from './domain/stage'
import { Dashboard } from './pages/Dashboard'
import { Wizard } from './pages/Wizard'
import { setupWechatShare } from './lib/wechat-share'

/** Dev 模式下，每个阶段对应的"虚拟今天"，用于演示。 */
const VIRTUAL_NOW_BY_STAGE: Record<LifeStage, Date> = {
  pre_exam:        new Date('2026-04-15T10:00:00+08:00'), // 远早于考试，倒计时 ~60 天
  near_exam:       new Date('2026-05-25T10:00:00+08:00'), // 临近考试
  in_exam:         new Date('2026-06-14T12:00:00+08:00'), // 考试中
  wait_score:      new Date('2026-06-20T10:00:00+08:00'), // 等待出分
  after_score:     new Date('2026-06-24T15:00:00+08:00'), // 已出分，等待官方分段表
  fill_window:     new Date('2026-06-28T10:00:00+08:00'), // 填报中
  after_admission: new Date('2026-07-15T10:00:00+08:00')  // 录取后
}

export default function App() {
  const { year, score, heat, conservativeLevel, densityMethod, intentSchool, wizardStep, probabilityModel } =
    useApp()
  const policy = POLICIES_MAP[year]

  // 并行对比用：URL ?probModel=bayesian / ?priorK=NN 可覆盖 store，便于在真实分数上新旧对照
  const { effModel, priorK } = useMemo(() => {
    let m: 'ratio' | 'bayesian' = probabilityModel
    let k: number | undefined
    if (typeof window !== 'undefined') {
      const p = new URLSearchParams(window.location.search)
      const qm = p.get('probModel')
      if (qm === 'bayesian' || qm === 'ratio') m = qm
      const qk = Number(p.get('priorK'))
      if (Number.isFinite(qk) && qk > 0) k = qk
    }
    return { effModel: m, priorK: k }
  }, [probabilityModel])
  const stageInfo = useMemo(() => {
    const currentYearHasData = POLICIES_MAP[2026]?.qualification_line !== null
    // Dev-only：?devStage=fill_window 强制阶段，便于演示。
    // 把虚拟"今天"传进 detectStage，能保证 milestoneAt 也被正确推算（毫秒倒计时不错配）
    if (typeof window !== 'undefined' && import.meta.env.DEV) {
      const params = new URLSearchParams(window.location.search)
      const force = params.get('devStage') as LifeStage | null
      if (force) {
        const virtualNow = VIRTUAL_NOW_BY_STAGE[force]
        if (virtualNow) {
          const info = detectStage(virtualNow, currentYearHasData)
          return { ...info, label: '【演示】' + info.label }
        }
      }
    }
    return detectStage(new Date(), currentYearHasData)
  }, [])

  const result = useMemo(() => {
    if (score === null || policy.qualification_line === null) return null
    try {
      return calculate({
        score,
        policy,
        preference: PREFERENCE,
        narrative: NARRATIVE,
        heatAdjust: heat,
        conservativeLevel,
        densityMethod,
        intentSchool,
        probabilityModel: effModel,
        priorStrength: priorK
      })
    } catch (e) {
      console.error('[zhcz] calculate threw:', e, { score, year: policy.year, intentSchool })
      const w = window as any
      if (w.Sentry?.captureException) w.Sentry.captureException(e)
      return null
    }
  }, [score, policy, heat, conservativeLevel, densityMethod, intentSchool, effModel, priorK])

  // 进度回滚：仅在 score 也丢失时才回 idle
  // （避免 calculate 偶发抛错或 useMemo 在某帧暂时为 null 时把整个 wizard 错误地清零）
  useEffect(() => {
    if (wizardStep === 'result' && score === null) {
      useApp.getState().setWizardStep('idle')
    }
  }, [wizardStep, score])

  // 微信内自定义分享卡（不在微信内 / 未配置则静默）
  useEffect(() => {
    void setupWechatShare()
  }, [])

  return (
    <div className="min-h-screen bg-ink-50">
      {wizardStep !== 'idle' && wizardStep !== 'result' ? (
        <Wizard policy={policy} result={result} />
      ) : (
        <Dashboard
          stageInfo={stageInfo}
          policy={policy}
          result={result}
        />
      )}
    </div>
  )
}
